import React, { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';

import { Skeleton, Table, TableProps } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';

import classNames from 'classnames';
import ResizeObserver from 'rc-resize-observer';
import { useTranslation } from 'react-i18next';
import { areEqual, VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import { EmptyGroup } from 'shared/ui/EmptyGroup';

import { ICustomColumnType, IVirtualTableProps } from './VirtualTable.types';
import styles from './VirtualTable.module.scss';

function VirtualTable<T extends object>({
  columns,
  dataSource,
  rowHeight,
  loadNextPage,
  hasNextPage,
  isNextPageLoading,
  threshold = 10,
  onSort,
  loaderRef,
  rowWrapper,
  showHeader = true,
  ...restProps
}: IVirtualTableProps<T>): React.ReactElement {
  const { t } = useTranslation();

  const [currentHeight, setCurrentHeight] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);
  const getRemainingWidthForColumnsWithoutWidth = useCallback((): number => {
    return columns.reduce((sum, column) => {
      switch (typeof column.width) {
        case 'string':
          return sum - (tableWidth * parseFloat(column.width)) / 100;
        case 'number':
          return sum - column.width;
        default:
          return sum;
      }
    }, tableWidth);
  }, [columns, tableWidth]);

  const extendedColumns = useMemo((): ICustomColumnType<T>[] => {
    const remainingWidth = getRemainingWidthForColumnsWithoutWidth();
    const columnsWithoutWidthCount = columns.filter(({ width }) => !width).length;

    return columns.map((column) => {
      if (column.width) {
        return {
          ...column,
          title: column?.title ? t(column.title) : undefined,
          width: typeof column.width === 'number' ? column.width : (tableWidth * parseFloat(column.width)) / 100,
        };
      }

      return {
        ...column,
        title: column?.title ? t(column.title) : undefined,
        width: remainingWidth / columnsWithoutWidthCount,
      };
    });
  }, [columns, getRemainingWidthForColumnsWithoutWidth, t, tableWidth]);

  const minColumnsWidth = useMemo(() => {
    return extendedColumns.reduce((acc, column) => {
      return acc + (typeof column.minWidth === 'number' ? column.minWidth : 0);
    }, 0);
  }, [extendedColumns]);

  const itemCount = hasNextPage ? dataSource.length + 1 : dataSource.length;
  const loadMoreData = useMemo(
    () => (isNextPageLoading ? () => null : loadNextPage),
    [isNextPageLoading, loadNextPage],
  );
  const isItemLoaded = useCallback(
    (index: number): boolean => !hasNextPage || index < dataSource?.length,
    [dataSource, hasNextPage],
  );

  const renderCell = useCallback((column: ICustomColumnType<T>, data: T, index: number): ReactNode => {
    const key = column.dataIndex || column.key || index;

    const dataToRender = key === index && typeof key === 'number' ? Object.values(data)[key] : data[key as keyof T];

    return column.render ? column.render(dataToRender, data, index) : dataToRender;
  }, []);

  const renderRow = useCallback(
    (data: T, rowIndex: number): ReactElement[] => {
      return extendedColumns.map((column, cellIndex) => {
        return (
          <div
            key={column.key}
            style={{
              width: column.width,
              height: rowHeight,
            }}
            className={classNames(styles.tableCell, 'ant-table-cell')}
            data-testid="table-cell"
          >
            {isItemLoaded(rowIndex) ? (
              renderCell(column, data, cellIndex)
            ) : (
              <Skeleton active paragraph={{ rows: 0 }} title={{ width: '100%' }} className={styles.skeleton} />
            )}
          </div>
        );
      });
    },
    [extendedColumns, isItemLoaded, renderCell, rowHeight],
  );

  const renderVirtualList = (rawData: readonly T[]): React.ReactNode => {
    return (
      <InfiniteLoader
        ref={loaderRef}
        isItemLoaded={isItemLoaded}
        itemCount={itemCount}
        loadMoreItems={loadMoreData}
        threshold={threshold}
        minimumBatchSize={5}
      >
        {({ onItemsRendered, ref: listRef }) => (
          <List
            ref={listRef}
            className="ant-table-tbody"
            height={showHeader ? currentHeight - rowHeight : currentHeight}
            itemSize={() => rowHeight}
            itemCount={itemCount}
            onItemsRendered={onItemsRendered}
            width={tableWidth}
          >
            {({ style, index }) => (
              <div
                style={{ ...style }}
                className={classNames('ant-table-row', 'ant-table-row-level-0', styles.tableRow)}
                data-testid="table-row"
              >
                {rowWrapper
                  ? rowWrapper(renderRow(rawData[index], index), rawData[index])
                  : renderRow(rawData[index], index)}
              </div>
            )}
          </List>
        )}
      </InfiniteLoader>
    );
  };

  const renderEmptyList = (): React.ReactNode => {
    return (
      <div style={{ height: currentHeight - rowHeight }} className={styles.emptyList}>
        <EmptyGroup />
      </div>
    );
  };

  const onChange: TableProps<T>['onChange'] = (_, __, sorter, extra) => {
    if (extra.action === 'sort') {
      const currentSorter = sorter as SorterResult<T>;
      onSort?.(currentSorter?.columnKey, currentSorter.order);
    }
  };

  return (
    <ResizeObserver
      onResize={({ width, height }) => {
        setTableWidth(width - 2);
        setCurrentHeight(height);
      }}
    >
      <Table
        columns={extendedColumns}
        pagination={false}
        components={{
          body: itemCount === 0 && !isNextPageLoading ? renderEmptyList : renderVirtualList,
        }}
        bordered
        scroll={{ y: currentHeight }}
        dataSource={dataSource}
        showSorterTooltip={false}
        onChange={onChange}
        showHeader={showHeader}
        style={{
          minWidth: minColumnsWidth,
        }}
        /* eslint-disable react/jsx-props-no-spreading */
        {...restProps}
      />
    </ResizeObserver>
  );
}

export default React.memo(VirtualTable, areEqual) as typeof VirtualTable;
